// Utility functions for marking and measuring performance using the User Timing API
// and integrating with SpeedCurve RUM if available.

import getPrimaryAssetType from '../article/get-primary-asset-type';

// Low traffic sites that need force sampling so we regularly get RUM data into SpeedCurve.
const forceSampleSites = [
    // fn & l
    'kidspot',
    'bodyandsoul',
    'escape',

    // s & c
    'codesports',
    'ntnews',
    'cairnspost',
    'townsvillebulletin',
    'goldcoastbulletin',
    'geelongadvertiser',
    'themercury',
    'weeklytimesnow',
    'thechronicle',

    // prestige
    'gq',
    'vogue'
];

/**
 * Records a User Timing mark and mirrors it into the SpeedCurve marks namespace.
 *
 * @param {String} markName - The name of the mark.
 * @param {Number} [startTime] - An optional explicit timestamp (ms since navigation start) to
 *   record the mark at, used when the event being marked occurred earlier than the call.
 */
export function markPerformance (markName, startTime) {
    try {
        if (window.performance && typeof window.performance.mark === 'function') {
            const entry = typeof startTime === 'number'
                ? window.performance.mark(markName, { startTime })
                : window.performance.mark(markName);
            const value = Math.round(entry.startTime);

            if (window.newscorpau?.speedcurve?.marks) {
                window.newscorpau.speedcurve.marks[markName] = value;
            }
            console.debug(`[speedcurve] mark: ${markName} at ${value}ms`);
        }
    } catch (error) {
        console.warn(`[speedcurve] mark failed for ${markName}:`, error);
    }
}

export function measurePerformance (measureName, startMark, endMark) {
    try {
        if (window.performance && typeof window.performance.measure === 'function') {
            const entry = window.performance.measure(measureName, startMark, endMark);
            const value = Math.round(entry.duration);

            if (window.newscorpau?.speedcurve?.measures) {
                if (!Array.isArray(window.newscorpau.speedcurve.measures[measureName])) {
                    window.newscorpau.speedcurve.measures[measureName] = [];
                }
                window.newscorpau.speedcurve.measures[measureName].push(value);
            }
            console.debug(`[speedcurve] measure: ${measureName} duration ${value}ms`);
        }
    } catch (error) {
        console.warn(`[speedcurve] measure failed for ${measureName}:`, error);
    }
}

export function forceLUXSample () {
    console.debug('[speedcurve] Checking if LUX forceSample should be enabled', window.LUX, window.newscorpau?.environment, window.newscorpau?.site);
    try {
        const isNonProduction = window.newscorpau?.environment !== 'production' && window.newscorpau?.environment !== 'prod';
        const isForceSampleSite = forceSampleSites.includes(window.newscorpau?.site);

        if (isNonProduction || isForceSampleSite) {
            const tryForceSample = (attempt = 1) => {
                if (window.LUX && typeof window.LUX.forceSample === 'function') {
                    window.LUX.forceSample();
                    console.debug(`[speedcurve] forceSample enabled. isNonProduction: ${isNonProduction}, isForceSampleSite: ${isForceSampleSite}`);
                } else if (attempt < 5) {
                    console.debug(`[speedcurve] LUX.forceSample unavailable, retrying in 200ms (attempt ${attempt}/5)...`);
                    setTimeout(() => tryForceSample(attempt + 1), 200);
                } else {
                    console.debug(`[speedcurve] forceSample not enabled after ${attempt} attempts. isNonProduction: ${isNonProduction}, isForceSampleSite: ${isForceSampleSite}`);
                }
            };
            tryForceSample();
        } else {
            console.debug(`[speedcurve] forceSample not enabled. isNonProduction: ${isNonProduction}, isForceSampleSite: ${isForceSampleSite}`);
        }
    } catch (error) {
        console.warn('Failed to force LUX sample:', error);
    }
}

export function addLUXData (eventName, data) {
    try {
        if (window.LUX && typeof window.LUX.addData === 'function') {
            // window.LUX.addData(eventName, data);
            // console.debug(`[speedcurve] data added for: ${eventName}`, data);
            console.debug(`[speedcurve] addLUXData currently disabled for ${eventName} with data:`, data);
        }
    } catch (error) {
        console.warn(`Failed to add LUX data for ${eventName}:`, error);
    }
}

// Initialises SpeedCurve RUM: enables force sampling where required and registers the
// custom dimensions used to segment the data. Called once early from the critical bundle.
export function initPerformance () {
    const newscorpau = window.newscorpau;
    // disabling force sample temporarily
    // forceLUXSample();

    // addLUXData('video_resource_prioritisation_enabled', newscorpau?.featureToggles?.is_video_resource_prioritisation_enabled);
    // addLUXData('video_ad_resource_prioritisation_enabled', newscorpau?.featureToggles?.is_video_ad_resource_prioritisation_enabled);
    // addLUXData('decouple_tad_from_utag_enabled', newscorpau?.featureToggles?.is_decouple_tad_from_utag_enabled);
    // addLUXData('decouple_utag_from_rampart_enabled', newscorpau?.featureToggles?.is_decouple_utag_from_rampart_enabled);
    // addLUXData('user-status', newscorpau?.userStatus || 'unknown');

    if (newscorpau?.pagetype === 'article') {
        newscorpau.primaryAssetType = getPrimaryAssetType();
        // addLUXData('primary-asset-type', newscorpau.primaryAssetType);
    }

    const [nav] = window.performance?.getEntriesByType?.('navigation') || [];
    const ttfb = nav && (nav.finalResponseHeadersStart || (!nav.firstInterimResponseStart ? nav.responseStart : 0));
    if (Number.isFinite(ttfb) && ttfb > 0) {
        measurePerformance('true-time-to-first-byte', { start: 0, end: ttfb });
    }
    console.debug('[speedcurve] initPerformance completed, addLUXData currently disabled');
}
